import { Storage } from 'aws-amplify';

// Ad-hoc document uploads for notifications. Files go to a dedicated
// notification-docs/ folder in dst-tmp — the one bucket the browser's
// Cognito role can write (see the S3WriteDstTmp IAM policy) — via the same
// Storage.put path the equipment photo upload uses. Objects are public-read,
// and the link we hand out is the direct S3 URL: browsers (including iOS
// Safari) render PDFs and images natively, and the Media Viewer's generic
// route can't display PDFs inline today.
const BUCKET_URL = 'https://dst-tmp.s3-us-west-2.amazonaws.com';
const FOLDER = 'notification-docs/';

export const MAX_DOC_BYTES = 25 * 1024 * 1024; // 25MB

const ALLOWED_EXTENSIONS = [
  'pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp',
  'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'txt',
];

export default class NotificationDocs {
  static isAllowedFile(file) {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    return ALLOWED_EXTENSIONS.includes(extension);
  }

  static allowedExtensionsLabel() {
    return ALLOWED_EXTENSIONS.join(', ');
  }

  // "Firmware Notice (v2).pdf" -> "Firmware Notice (v2)" — the link label
  static labelFromFile(file) {
    return file.name.replace(/\.[^.]+$/, '').trim() || file.name;
  }

  // Collision-proof, identifiable object name: 2026-09-16-firmware-notice-v2-a1b2.pdf
  static remoteFilename(file) {
    const extension = (file.name.split('.').pop() || 'pdf').toLowerCase();
    const slug = file.name
      .replace(/\.[^.]+$/, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'document';
    const date = new Date().toISOString().slice(0, 10);
    const suffix = Math.random().toString(36).slice(2, 6);
    return `${date}-${slug}-${suffix}.${extension}`;
  }

  // S3 metadata values must be US-ASCII and small; keep them safe and capped.
  static sanitizeMetadataValue(value, maxLength = 500) {
    return `${value || ''}`
      .replace(/[^\x20-\x7E]/g, '')
      .trim()
      .slice(0, maxLength);
  }

  // Uploads the file and returns { label, url } ready for a notification
  // link row. Uploader identity and description are stamped onto the S3
  // object as metadata (x-amz-meta-*), so provenance lives with the file
  // itself — visible in the S3 console under the object's properties.
  // (The notification links column can't carry them: the backend normalizes
  // links down to {label, url}.) Throws on failure.
  static upload = async (file, { label, description, uploadedByEmail, uploadedByName } = {}, onProgress) => {
    const fileName = this.remoteFilename(file);
    const customPrefix = { public: FOLDER, protected: FOLDER, private: FOLDER };
    await Storage.put(fileName, file, {
      customPrefix,
      contentType: file.type || 'application/octet-stream',
      level: 'public',
      acl: 'public-read',
      metadata: {
        'uploaded-by': this.sanitizeMetadataValue(uploadedByEmail, 120),
        'uploaded-by-name': this.sanitizeMetadataValue(uploadedByName, 120),
        'description': this.sanitizeMetadataValue(description),
        'original-filename': this.sanitizeMetadataValue(file.name, 200),
      },
      progressCallback: (progress) => {
        if (onProgress && progress.total) {
          onProgress(Math.round((progress.loaded / progress.total) * 100));
        }
      },
    });
    return {
      label: (label || '').trim() || this.labelFromFile(file),
      url: `${BUCKET_URL}/${FOLDER}${encodeURIComponent(fileName)}`,
    };
  };
}
